import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PostCard from "@/components/feature/PostCard";
import { allCategories, getCategoryBySlug, getPostsByCategory } from "@/lib/content";
import { toneBadge, toneDot } from "@/lib/tone";
import type { Post } from "@/types/content";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-emotion-unhappy-line text-5xl text-primary-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经调整了名称，回到首页看看其他内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const basePosts = getPostsByCategory(category.slug);
  const posts: Post[] =
    sort === "hot" ? [...basePosts].sort((a, b) => b.views - a.views) : [...basePosts].sort((a, b) => (a.date < b.date ? 1 : -1));

  const hotInCategory = [...basePosts].sort((a, b) => b.views - a.views).slice(0, 5);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name} — 瓜乐圈`,
      description: category.description,
      url: `${SITE_URL}/category/${category.slug}`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: posts.map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/post/${post.id}`,
          name: post.title,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: category.name, item: `${SITE_URL}/category/${category.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${category.name}专栏 — 福利吃瓜${category.name}合集 | 瓜乐圈`}
        description={`${category.description}瓜乐圈「${category.name}」栏目共收录 ${basePosts.length} 篇内容，按最新与最热整理，碎片时间随手刷一刷。`}
        keywords={`福利吃瓜,${category.name},${category.name}合集,瓜乐圈${category.name}`}
        path={`/category/${category.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
            <div className="mt-6 flex flex-col gap-5 md:flex-row md:items-center">
              <span
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl2 ${toneDot[category.tone]} text-background-50`}
              >
                <i className={`${category.icon} text-2xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
                  {category.name}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  {category.description}
                </p>
              </div>
              <div className="md:ml-auto">
                <span className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${toneBadge[category.tone]}`}>
                  <i className="ri-stack-line"></i>
                  共 {basePosts.length} 篇
                </span>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <h2 className="font-heading text-sm font-bold tracking-wide text-foreground-950">切换栏目</h2>
                <nav aria-label="栏目切换" className="mt-3 flex flex-row gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
                  {allCategories.map((item) => {
                    const active = item.slug === category.slug;
                    return (
                      <Link
                        key={item.slug}
                        to={`/category/${item.slug}`}
                        className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm transition-colors ${
                          active
                            ? "bg-primary-500 font-medium text-background-50"
                            : "border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                        }`}
                      >
                        <i className={`${item.icon} text-base`}></i>
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>

                <div className="mt-8 rounded-xl2 border border-background-200 bg-background-50 p-4">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">本栏热门</h2>
                  <ul className="mt-3 space-y-3">
                    {hotInCategory.map((post, index) => (
                      <li key={post.id}>
                        <Link
                          to={`/post/${post.id}`}
                          className="group flex items-start gap-3 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                        >
                          <span className="mt-0.5 font-heading text-xs font-bold text-primary-500">{index + 1}</span>
                          <span className="flex-1 leading-snug">{post.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex flex-col gap-4 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground-600">
                  共 <strong className="font-medium text-foreground-950">{posts.length}</strong> 篇内容
                </p>
                <div className="inline-flex w-fit items-center gap-1 rounded-full bg-background-100 p-1">
                  {[
                    { key: "latest" as const, label: "最新更新" },
                    { key: "hot" as const, label: "最多浏览" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                        sort === option.key
                          ? "bg-background-50 font-medium text-primary-700"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                {posts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>

              {posts.length === 0 && (
                <p className="rounded-xl2 border border-dashed border-background-300 py-16 text-center text-sm text-foreground-500">
                  这个栏目还在整理内容，稍后回来看看。
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}